import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Plus,
  Send,
  Trash2,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  XCircle,
  FileDown,
  Zap,
  ChevronRight,
  Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth';
import {
  usePrestacoes,
  useDeletePrestacao,
  useGeneratePrestacaoPdf,
  type Prestacao,
  type PrestacaoStatus,
} from '@/lib/hooks/prestacoes';
import { PrestacaoDrawer } from '@/features/prestacoes/PrestacaoDrawer';
import { fmtDate, fmtDateTime } from '@/lib/utils';

export const Route = createFileRoute('/_app/prestacoes')({
  component: PrestacoesPage,
});

const STATUS_LABEL: Record<PrestacaoStatus, string> = {
  RASCUNHO: 'rascunho',
  PRONTA: 'pronta para envio',
  AUTOMATING: 'automatizando',
  AGUARDANDO_HUMANO: 'aguardando você',
  CONCLUIDA: 'concluída',
  ERRO: 'erro',
  CANCELADA: 'cancelada',
};

const STATUS_TONE: Record<PrestacaoStatus, 'default' | 'mint' | 'pass' | 'warn' | 'block' | 'ink'> = {
  RASCUNHO: 'default',
  PRONTA: 'mint',
  AUTOMATING: 'mint',
  AGUARDANDO_HUMANO: 'warn',
  CONCLUIDA: 'pass',
  ERRO: 'block',
  CANCELADA: 'default',
};

/** Extrai mês (0-based) e ano de "YYYY-MM" */
function parseCompetencia(competencia: string): { mo: string; yr: string; label: string } {
  const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const [yrStr, moStr] = competencia.split('-');
  const moIdx = parseInt(moStr ?? '1', 10) - 1;
  const mo = MESES[moIdx] ?? moStr ?? '';
  const yr = yrStr ?? '';
  const label = `${mo}/${yr.slice(2)}`;
  return { mo, yr, label };
}

/** Encontra a "próxima prestação" — RASCUNHO ou PRONTA mais recente; fallback: item[0] */
function findProxima(items: Prestacao[]): Prestacao | null {
  if (items.length === 0) return null;
  const pending = items.filter(
    (p) => p.status === 'RASCUNHO' || p.status === 'PRONTA',
  );
  if (pending.length > 0) return pending[0] ?? null;
  return items[0] ?? null;
}

/** Skeleton de um prest-card para estado de loading */
function PrestCardSkeleton() {
  return (
    <div className="prest-card" aria-hidden>
      <div className="month-tile">
        <div className="skel w-8 h-4 rounded-1" />
        <div className="skel w-6 h-2 rounded-1 mt-1" />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="skel h-3.5 w-40 rounded-1" />
        <div className="skel h-2.5 w-28 rounded-1" />
      </div>
      <div className="skel h-5 w-20 rounded-pill" />
      <div className="skel h-3 w-12 rounded-1" />
      <div className="skel h-7 w-7 rounded-1" />
    </div>
  );
}

function PrestacoesPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Prestacao | null>(null);

  const listQ = usePrestacoes(workspaceId);
  const delMut = useDeletePrestacao(workspaceId);
  const pdfMut = useGeneratePrestacaoPdf(workspaceId);

  async function generatePdf(id: string) {
    setOpenRow(null);
    try {
      const r = await pdfMut.mutateAsync(id);
      toast.success('PDF gerado.');
      window.open(r.url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar PDF.');
    }
  }

  const items = listQ.data?.items ?? [];

  const counts = {
    pronta: items.filter((p) => p.status === 'PRONTA').length,
    aguardando: items.filter((p) => p.status === 'AGUARDANDO_HUMANO').length,
    concluida: items.filter((p) => p.status === 'CONCLUIDA').length,
    erro: items.filter((p) => p.status === 'ERRO').length,
  };

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Prestação removida.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para gerenciar prestações.</p>
      </Card>
    );
  }

  const proxima = !listQ.isLoading ? findProxima(items) : null;
  const proximaComp = proxima ? parseCompetencia(proxima.competencia) : null;
  const proximaIsPending =
    proxima?.status === 'RASCUNHO' || proxima?.status === 'PRONTA';

  return (
    <div className="flex flex-col gap-5">
      {/* ── Hero mint ─────────────────────────────────────────────── */}
      <Card variant="mint" className="p-7">
        <Eyebrow className="opacity-70">Próxima prestação</Eyebrow>

        {proxima ? (
          <>
            <p className="font-display text-[28px] md:text-[32px] font-bold tracking-[-0.025em] leading-[1.05] mt-2.5 mb-1">
              {proxima.competencia.replace('-', '/')}
            </p>
            <p className="text-sm opacity-80 mb-5">
              {STATUS_LABEL[proxima.status]}
              {proxima.concluidaEm ? ` · concluída em ${fmtDate(proxima.concluidaEm)}` : ''}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="bg-white/20 hover:bg-white/30 border-white/30 text-on-mint font-semibold"
                onClick={() => setDrawerOpen(true)}
              >
                <Plus className="h-4 w-4" strokeWidth={1.8} />
                Nova prestação
              </Button>
              {proximaIsPending && (
                <Link to="/prestacao-live/$id" params={{ id: proxima.id }}>
                  <Button
                    variant="primary"
                    size="sm"
                    className="bg-mint-deep/90 hover:bg-mint-deep border-transparent text-on-mint font-semibold shadow-none"
                  >
                    <Zap className="h-4 w-4" strokeWidth={1.8} />
                    Iniciar {proximaComp?.label}
                  </Button>
                </Link>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="font-display text-[28px] font-bold tracking-[-0.025em] leading-[1.05] mt-2.5 mb-1">
              Você está em dia
            </p>
            <p className="text-sm opacity-80 mb-5">
              Nenhuma prestação pendente no momento.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="bg-white/20 hover:bg-white/30 border-white/30 text-on-mint font-semibold"
              onClick={() => setDrawerOpen(true)}
            >
              <Plus className="h-4 w-4" strokeWidth={1.8} />
              Nova prestação
            </Button>
          </>
        )}
      </Card>

      {/* ── KPI strip ─────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI tone="mint" label="Prontas pra disparar" value={String(counts.pronta)} icon={Send} />
        <KPI tone="clay" label="Aguardando você" value={String(counts.aguardando)} icon={Clock} />
        <KPI tone="ink" label="Concluídas" value={String(counts.concluida)} icon={CheckCircle2} />
        <KPI label="Em erro" value={String(counts.erro)} icon={XCircle} />
      </section>

      {/* ── Stack de prest-cards ──────────────────────────────────── */}
      <section className="flex flex-col gap-2" aria-label="Lista de prestações">
        {listQ.isLoading && (
          <>
            <PrestCardSkeleton />
            <PrestCardSkeleton />
            <PrestCardSkeleton />
          </>
        )}

        {!listQ.isLoading && items.length === 0 && (
          <Card variant="default" className="p-10 text-center">
            <Receipt
              className="mx-auto mb-4 text-fg-muted"
              style={{ width: 40, height: 40 }}
              strokeWidth={1.4}
            />
            <p className="text-sm font-medium text-fg mb-1">Nenhuma prestação ainda</p>
            <p className="text-xs text-fg-muted mb-5">
              Crie a primeira prestação e deixe a automação trabalhar por você.
            </p>
            <Button variant="primary" size="sm" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.8} />
              Nova prestação
            </Button>
          </Card>
        )}

        {!listQ.isLoading &&
          items.map((p, idx) => {
            const { mo, yr } = parseCompetencia(p.competencia);
            const isFirst = idx === 0;
            const metaSub = p.concluidaEm
              ? `concluída em ${fmtDateTime(p.concluidaEm)}`
              : p.iniciadaEm
              ? `iniciada em ${fmtDate(p.iniciadaEm)}`
              : `criada em ${fmtDate(p.createdAt)}`;

            return (
              <div
                key={p.id}
                className={`prest-card${isFirst ? ' float' : ''}`}
                role="row"
              >
                {/* Tile mês */}
                <div className="month-tile" aria-hidden>
                  <span className="mo">{mo}</span>
                  <span className="yr">{yr}</span>
                </div>

                {/* Meta — clicável para acompanhar / iniciar */}
                <Link
                  to="/prestacao-live/$id"
                  params={{ id: p.id }}
                  className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-2 rounded-1"
                  aria-label={`Abrir prestação ${p.competencia}`}
                >
                  <p className="meta-top">Prestação {p.competencia.replace('-', '/')}</p>
                  <p className="meta-sub">{metaSub}</p>
                </Link>

                {/* Status pill */}
                <Pill tone={STATUS_TONE[p.status]} dot>
                  {STATUS_LABEL[p.status]}
                </Pill>

                {/* Chevron indicador */}
                <ChevronRight
                  className="text-fg-muted shrink-0"
                  style={{ width: 16, height: 16 }}
                  strokeWidth={1.8}
                  aria-hidden
                />

                {/* Dropdown ações */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenRow(openRow === p.id ? null : p.id)}
                    className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                    aria-label="Ações da prestação"
                    aria-expanded={openRow === p.id}
                    aria-haspopup="menu"
                  >
                    <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                  {openRow === p.id && (
                    <>
                      <div
                        className="fixed inset-0 z-30"
                        onClick={() => setOpenRow(null)}
                        aria-hidden
                      />
                      <div
                        className="absolute right-0 top-9 z-40 w-48 rounded-2 border border-card-stroke bg-card shadow-clay p-1"
                        role="menu"
                      >
                        <Link
                          to="/prestacao-live/$id"
                          params={{ id: p.id }}
                          onClick={() => setOpenRow(null)}
                          className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-fg hover:bg-mint-soft hover:text-mint-deep"
                          role="menuitem"
                        >
                          <Zap className="h-4 w-4" strokeWidth={1.8} />
                          {p.status === 'AUTOMATING' || p.status === 'AGUARDANDO_HUMANO'
                            ? 'Acompanhar live'
                            : 'Iniciar automação'}
                        </Link>
                        <button
                          type="button"
                          onClick={() => generatePdf(p.id)}
                          disabled={pdfMut.isPending}
                          className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-fg hover:bg-mint-soft hover:text-mint-deep disabled:opacity-50"
                          role="menuitem"
                        >
                          <FileDown className="h-4 w-4" strokeWidth={1.8} />
                          Gerar PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenRow(null);
                            setConfirmDelete(p);
                          }}
                          className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                          role="menuitem"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                          Excluir
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </section>

      <PrestacaoDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir esta prestação?"
        description={
          confirmDelete
            ? `${confirmDelete.competencia} · status ${STATUS_LABEL[confirmDelete.status]}. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
